import { useMemo, useState } from 'react';

interface BranchHead {
  name: string;
  sha: string;
  color: string;
}

interface Commit {
  sha: string;
  message: string;
  author: string;
  date: string;
  parents: string[];
}

interface GraphData {
  branches: BranchHead[];
  commits: Commit[];
  totalCommits: number;
}

interface Props {
  data: GraphData;
}

const LANE_WIDTH = 30;
const ROW_HEIGHT = 32;
const NODE_RADIUS = 5;
const LEFT_PADDING = 20;

function BranchGraph({ data }: Props) {
  const [hoveredCommit, setHoveredCommit] = useState<string | null>(null);

  const { nodes, edges, svgWidth, svgHeight, branchLanes } = useMemo(() => {
    if (!data.commits.length || !data.branches.length) {
      return { nodes: [], edges: [], svgWidth: 400, svgHeight: 100, branchLanes: [] };
    }

    // Sort commits by date (newest first)
    const sorted = [...data.commits].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    // Assign lanes to branches
    const lanes: { name: string; color: string; shas: Set<string> }[] = [];
    for (const branch of data.branches) {
      const branchCommits = new Set<string>();
      // Find all commits reachable from this branch head
      const queue = [branch.sha];
      const visited = new Set<string>();
      while (queue.length > 0) {
        const sha = queue.shift()!;
        if (visited.has(sha)) continue;
        visited.add(sha);
        branchCommits.add(sha);
        const commit = sorted.find(c => c.sha === sha);
        if (commit) {
          for (const parent of commit.parents) {
            queue.push(parent);
          }
        }
      }
      lanes.push({ name: branch.name, color: branch.color, shas: branchCommits });
    }

    // Determine lane for each commit (first branch that contains it)
    const commitLane = new Map<string, number>();
    for (const commit of sorted) {
      for (let i = 0; i < lanes.length; i++) {
        if (lanes[i].shas.has(commit.sha)) {
          commitLane.set(commit.sha, i);
          break;
        }
      }
      // Default to lane 0 if not found in any branch
      if (!commitLane.has(commit.sha)) {
        commitLane.set(commit.sha, 0);
      }
    }

    // Build node positions
    const commitPosition = new Map<string, { x: number; y: number }>();
    const nodeList: { sha: string; x: number; y: number; color: string; commit: Commit }[] = [];

    sorted.forEach((commit, index) => {
      const lane = commitLane.get(commit.sha) || 0;
      const x = LEFT_PADDING + lane * LANE_WIDTH;
      const y = 20 + index * ROW_HEIGHT;
      commitPosition.set(commit.sha, { x, y });
      nodeList.push({
        sha: commit.sha,
        x,
        y,
        color: lanes[lane]?.color || '#6b7280',
        commit,
      });
    });

    // Build edges
    const edgeList: { x1: number; y1: number; x2: number; y2: number; color: string }[] = [];
    for (const commit of sorted) {
      const pos = commitPosition.get(commit.sha);
      if (!pos) continue;
      const lane = commitLane.get(commit.sha) || 0;
      for (const parentSha of commit.parents) {
        const parentPos = commitPosition.get(parentSha);
        if (parentPos) {
          edgeList.push({
            x1: pos.x,
            y1: pos.y,
            x2: parentPos.x,
            y2: parentPos.y,
            color: lanes[lane]?.color || '#6b7280',
          });
        }
      }
    }

    const maxLane = Math.max(...Array.from(commitLane.values()), 0);
    const width = Math.max(400, LEFT_PADDING + (maxLane + 1) * LANE_WIDTH + 300);
    const height = 40 + sorted.length * ROW_HEIGHT;

    return {
      nodes: nodeList,
      edges: edgeList,
      svgWidth: width,
      svgHeight: height,
      branchLanes: lanes.map((l, i) => ({ name: l.name, color: l.color, x: LEFT_PADDING + i * LANE_WIDTH })),
    };
  }, [data]);

  if (!data.commits.length) {
    return (
      <div className="text-center text-stone-500 py-8 text-sm">
        No commit data available
      </div>
    );
  }

  const graphAreaWidth = LEFT_PADDING + (branchLanes.length) * LANE_WIDTH + 20;

  return (
    <div className="overflow-auto max-h-[500px]" style={{ scrollbarWidth: 'thin' }}>
      {/* Branch labels */}
      <div className="flex gap-4 mb-3">
        {branchLanes.map(lane => (
          <div key={lane.name} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: lane.color }}
            />
            <span className="text-xs text-stone-400">{lane.name}</span>
          </div>
        ))}
      </div>

      <svg
        width={svgWidth}
        height={svgHeight}
        className="font-mono"
      >
        {/* Edges */}
        {edges.map((edge, i) => (
          <path
            key={i}
            d={
              edge.x1 === edge.x2
                ? `M ${edge.x1} ${edge.y1} L ${edge.x2} ${edge.y2}`
                : `M ${edge.x1} ${edge.y1} C ${edge.x1} ${edge.y1 + (edge.y2 - edge.y1) / 2}, ${edge.x2} ${edge.y1 + (edge.y2 - edge.y1) / 2}, ${edge.x2} ${edge.y2}`
            }
            stroke={edge.color}
            strokeWidth={2}
            fill="none"
            opacity={0.6}
          />
        ))}

        {/* Nodes */}
        {nodes.map(node => (
          <g
            key={node.sha}
            onMouseEnter={() => setHoveredCommit(node.sha)}
            onMouseLeave={() => setHoveredCommit(null)}
            className="cursor-pointer"
          >
            <circle
              cx={node.x}
              cy={node.y}
              r={hoveredCommit === node.sha ? NODE_RADIUS + 2 : NODE_RADIUS}
              fill={node.color}
              stroke={hoveredCommit === node.sha ? '#fff' : 'none'}
              strokeWidth={2}
            />
            {/* Commit info text */}
            <text
              x={graphAreaWidth}
              y={node.y + 4}
              className="text-[11px]"
              fill={hoveredCommit === node.sha ? '#fff' : '#a8a29e'}
            >
              <tspan fill="#78716c" className="font-mono">{node.sha.slice(0, 7)}</tspan>
              <tspan dx="8" fill={hoveredCommit === node.sha ? '#fff' : '#d6d3d1'}>
                {node.commit.message.slice(0, 60)}
                {node.commit.message.length > 60 ? '...' : ''}
              </tspan>
              <tspan dx="8" fill="#78716c">
                {node.commit.author}
              </tspan>
            </text>
          </g>
        ))}
      </svg>

      {/* Tooltip */}
      {hoveredCommit && (() => {
        const node = nodes.find(n => n.sha === hoveredCommit);
        if (!node) return null;
        return (
          <div
            className="fixed z-50 bg-dark-900 border border-dark-600 rounded-lg shadow-lg p-3 max-w-sm pointer-events-none"
            style={{
              left: 'var(--mouse-x, 0)',
              top: 'var(--mouse-y, 0)',
            }}
          >
            <p className="text-xs text-stone-300 font-mono">{node.sha.slice(0, 12)}</p>
            <p className="text-sm text-white mt-1">{node.commit.message}</p>
            <p className="text-xs text-stone-500 mt-1">
              {node.commit.author} · {new Date(node.commit.date).toLocaleString()}
            </p>
          </div>
        );
      })()}
    </div>
  );
}

export default BranchGraph;
